import {
  Box,
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { CustomStepperIcon } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Status } from 'Modules/Equipment/EquipmentMain/EquipmentRecentOrders/Status';
import { ReactElement, useEffect } from 'react';

interface ICustomStepper {
  steps: { name: string; endDate?: string }[];
  lastStepIndex?: number;
  activeStepIndex: number;
  setActiveStepIndex?: (index: number) => void;
  isDenied?: boolean;
  slideContent?: ReactElement;
}

const CustomStepMobile = styled(StepConnector)(() => ({
  marginLeft: '12.5px',
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#A020FF',
      minHeight: '40px',
    },
  },
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      minHeight: '40px',
    },
  },
}));

const CustomStepDesktop = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#E4C1FF',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#A020FF',
    },
  },
}));

const CustomConnector = () => {
  const isDesktop = useIsDesktop();
  return isDesktop ? <CustomStepDesktop /> : <CustomStepMobile />;
};

export const CustomStepper = ({
  steps,
  activeStepIndex,
  lastStepIndex = activeStepIndex,
  setActiveStepIndex = (number: number) => {},
  isDenied = false,
  slideContent,
}: ICustomStepper) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Status);

  useEffect(() => {
    setActiveStepIndex(lastStepIndex);
  }, [lastStepIndex, setActiveStepIndex]);

  return isDesktop ? (
    <Stepper alternativeLabel connector={<CustomConnector />}>
      {steps.map((step, index) => (
        <Step
          key={step.name}
          completed={index <= lastStepIndex}
          sx={{
            flex: '1 1 0',
            width: '0',
          }}
          active={index === activeStepIndex}
        >
          <StepLabel
            StepIconComponent={() =>
              CustomStepperIcon({
                active: index === activeStepIndex,
                completed: index <= lastStepIndex,
                isLastStep: index === lastStepIndex,
                isDenied,
              })
            }
            role='radio'
            onClick={
              index <= lastStepIndex
                ? () => setActiveStepIndex(index)
                : undefined
            }
            onKeyDown={
              index <= lastStepIndex
                ? (e) => {
                    if (e.key === 'Enter') setActiveStepIndex(index);
                  }
                : undefined
            }
            sx={{
              cursor: index <= lastStepIndex ? 'pointer !important' : undefined,
            }}
          >
            <Typography
              fontSize={18}
              fontWeight={index === activeStepIndex ? 700 : ''}
              color={index === activeStepIndex ? '#1752AF' : ''}
            >
              {index === activeStepIndex && (
                <Box sx={visuallyHidden}>{dictionary.currentStep}</Box>
              )}
              {step.name}
            </Typography>
            {step.endDate && (
              <Typography>{dictionary.stepEndedAt + step.endDate}</Typography>
            )}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  ) : (
    <Stepper
      orientation='vertical'
      connector={<CustomConnector />}
      sx={{ m: '0 16px', top: '-65px', position: 'relative' }}
    >
      {steps.map((step, index) => (
        <>
          <Step
            key={step.name}
            completed={index <= lastStepIndex}
            active={index === activeStepIndex}
            sx={{ width: '100%' }}
          >
            <StepLabel
              StepIconComponent={CustomStepperIcon}
              onClick={
                index <= lastStepIndex
                  ? () => setActiveStepIndex(index)
                  : undefined
              }
              sx={{
                position: 'relative',
                p: 0,
                top: index === lastStepIndex ? '6px' : '',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '0',
                }}
              >
                {index === activeStepIndex ? (
                  <Status status={index} statusLabel={step.name} />
                ) : (
                  <Typography variant='body5'>{step.name}</Typography>
                )}
              </Box>
            </StepLabel>
          </Step>
          {index === activeStepIndex && slideContent && (
            <Stack
              direction='row'
              borderLeft={`1px solid ${
                index < lastStepIndex && isDesktop ? '#A020FF' : '#A020FF'
              }`}
              ml='12px'
              mt='18px'
              pl='24px'
              pr='10px'
              pt='12px'
            >
              {slideContent}
            </Stack>
          )}
        </>
      ))}
    </Stepper>
  );
};
